import { connect } from 'react-redux';
import { bindActionCreators, type Dispatch } from 'redux';
import type { IPaymentMethod } from '@shore/types';
import { setIntercomReady, setHubspotReady } from '../../actions';
import {
  DELIGHTED,
  COMPANY_NAME,
  HUBSPOT_ID,
  INTERCOM_APP_ID,
} from '../../constants';
import { isPaidMerchant } from '../../selectors/subscription';
import Widgets from '../../components/Widgets';
import { location } from '../../env/browser';
import type { RootState } from '../../reducers';
import type { CurrentAccountState } from '../../reducers/currentAccount';
import type { CurrentMerchantState } from '../../reducers/currentMerchant';
import type { MerchantRole } from '../../shared/types';

const PAYMENT_METHODS = ['card', 'sepa_debit', 'sepa_credit_transfer'];

const getSegmentedPaymentSource = (
  segment: string,
  account: CurrentAccountState
): string[] => {
  const { defaultPaymentSourceType } = account.contractDetails;
  return defaultPaymentSourceType &&
    PAYMENT_METHODS.includes(defaultPaymentSourceType)
    ? [segment, `${segment}-ProductInvoice`]
    : [segment];
};

const accountPackageMatches =
  (packageName: string) =>
  (account: CurrentAccountState): boolean =>
    account.contractDetails.packages.includes(packageName);

const isPlusPackageAccount = accountPackageMatches('shore_plus');
const isBasicPackageAccount = accountPackageMatches('shore_basic');

const getGenericBeamerSegments = (account: CurrentAccountState): string[] => {
  const segmentsMap: Record<string, (account: CurrentAccountState) => boolean> =
    {
      'Plus-Users': isPlusPackageAccount,
      'Basic-Users': isBasicPackageAccount,
      'Test-Accounts': ({ isTestAccount }) => isTestAccount,
    };
  return Object.keys(segmentsMap).reduce<string[]>(
    (acc, key) => (segmentsMap[key](account) ? [...acc, key] : acc),
    []
  );
};

const getBeamerSegments = (
  companyName: string,
  account: CurrentAccountState
): string[] => {
  switch (companyName) {
    case 'shore':
      return [
        ...getGenericBeamerSegments(account),
        ...getSegmentedPaymentSource('Shore-Users', account),
      ];

    default:
      return [];
  }
};

const startsWith =
  (str1: string) =>
  (str2: string): boolean =>
    str1.startsWith(str2);

interface RoleLookup {
  merchantId?: string | null;
  roles?: MerchantRole[];
}

export const getRoleNameForMerchantId = ({
  merchantId,
  roles,
}: RoleLookup): string | null => {
  const role =
    roles && roles.find((r) => r.merchant && r.merchant.id === merchantId);
  return role && role.name ? role.name : null;
};

export const getIsAccountTerminated = (contractStates?: string[]): boolean =>
  !!contractStates &&
  contractStates.includes('terminated') &&
  !contractStates.includes('accepted');

export const getHubspotChatflow = ({
  isPaidUser,
  locale,
}: {
  isPaidUser: boolean;
  locale: string;
}): string => {
  const chatflowUser = isPaidUser ? 'paid' : 'trial';
  const supportedLocales = isPaidUser
    ? ['en', 'es', 'de', 'it', 'fr']
    : ['en', 'es', 'de'];
  const chatflowLocale = supportedLocales.some(startsWith(locale))
    ? locale.slice(0, 2)
    : 'zu';

  return `bm_${chatflowUser}_${chatflowLocale}`;
};

interface BeamerAccountInput {
  organizationId?: string;
  organisationName?: string;
  roles?: RoleLookup['roles'];
  isKeyAccount?: boolean;
  contractDetails?: {
    packages?: string[];
    states?: string[];
  };
}

interface BeamerMerchantInput {
  id?: string | null;
  category?: string;
  isTest?: boolean;
  trialEndsOn?: string | null;
  pixelId?: string | number | null;
  paymentSettings?: { paymentMethods?: IPaymentMethod[] } | null;
}

export const createBeamerCustomAttributes = ({
  account: {
    organizationId,
    organisationName,
    roles,
    isKeyAccount,
    contractDetails: { packages, states } = {},
  } = {},
  merchant: {
    id,
    category,
    isTest,
    trialEndsOn,
    pixelId,
    paymentSettings,
  } = {},
}: {
  account?: BeamerAccountInput;
  merchant?: BeamerMerchantInput;
}) => {
  const userSPONStatus =
    paymentSettings?.paymentMethods?.find(
      (paymentMethod) => paymentMethod.name === 'shorepay_online'
    )?.status || 'not_setup';

  return {
    user_role: getRoleNameForMerchantId({ merchantId: id, roles }) || '',
    user_merchant_id: id || '',
    user_organization_id: organizationId || '',
    user_industry: category || '',
    user_account_type: isKeyAccount ? 'key' : isTest ? 'test' : '',
    user_plans: packages ? packages.join(';') : '',
    user_lifecycle_stage: trialEndsOn
      ? 'trial'
      : getIsAccountTerminated(states)
        ? 'terminated'
        : 'active',
    user_integrations: pixelId ? 'facebook;instagram' : '',
    user_key_account_name: isKeyAccount ? organisationName : '',
    user_spon_status: userSPONStatus,
  };
};

export const createDelightedCustomAttributes = ({
  account: { organisationName, roles, contractDetails: { packages } = {} } = {},
  merchant: { id, category } = {},
}: {
  account?: BeamerAccountInput;
  merchant?: Pick<BeamerMerchantInput, 'id' | 'category'>;
}) => ({
  user_role: getRoleNameForMerchantId({ merchantId: id, roles }) || '',
  user_merchant_id: id || '',
  user_industry: category || '',
  user_plans: packages ? packages.join(';') : '',
  user_organisation_name: organisationName,
});

const isCurrentUserAdmin = (
  currentAccount: CurrentAccountState,
  currentMerchant: CurrentMerchantState
) =>
  currentAccount.organizationAdmin ||
  currentAccount.roles
    .filter(({ merchant: { id } }) => id === currentMerchant.id)
    .reduce((acc, { name }) => acc || ['admin'].includes(name), false);

const mapStateToProps = (state: RootState) => {
  const { currentAccount, currentMerchant, currentSidebar } = state;

  const beamerCustomAttributes = createBeamerCustomAttributes({
    account: currentAccount,
    merchant: currentMerchant,
  });

  const delightedCustomAttributes = createDelightedCustomAttributes({
    account: currentAccount,
    merchant: currentMerchant,
  });

  const segments = getBeamerSegments(COMPANY_NAME, currentAccount);
  const hasMerchant = !!currentMerchant.id;

  const isPaidUser = isPaidMerchant(state);

  const { isKeyAccount, isTestAccount, locale } = currentAccount;
  const showHubspot =
    !currentSidebar.name && !!currentAccount.id && !!HUBSPOT_ID;
  const showDelighted =
    DELIGHTED && !isTestAccount && isPaidUser && !isKeyAccount;

  const showIntercomChatbot =
    isPaidUser &&
    !currentSidebar.name &&
    !!currentAccount.id &&
    !!INTERCOM_APP_ID &&
    hasMerchant;

  const whiteLabel = COMPANY_NAME;
  const activateHubSpot = Boolean(hasMerchant && currentAccount.organizationId);
  const isContractApp = !!(
    location &&
    location.pathname &&
    location.pathname.includes('contract')
  );
  const hubspotChatflow = getHubspotChatflow({
    isPaidUser,
    locale,
  });

  return {
    account: currentAccount,
    activateHubSpot,
    locale,
    merchant: currentMerchant,
    segments,
    showDelighted,
    showHubspot,
    showIntercomChatbot,
    isContractApp,
    isUserAdmin: isCurrentUserAdmin(currentAccount, currentMerchant),
    isPaidUser,
    isKeyAccount,
    hubspotChatflow,
    beamerCustomAttributes,
    delightedCustomAttributes,
    whiteLabel,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      onHubspotReady: setHubspotReady,
      onIntercomReady: setIntercomReady,
    },
    (action) => action && dispatch(action)
  );

export default connect(mapStateToProps, mapDispatchToProps)(Widgets);
